"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormControl, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { CustomPopover } from "@/components/ui/popover-custom";
import { useMediaQuery } from "@/hooks/use-media-query";
import { formatMoneyBr } from "@/utils/format";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { FaRegDotCircle } from "react-icons/fa";
import { LuPenLine } from "react-icons/lu";

interface Props<TFieldValues extends FieldValues = FieldValues> {
  tipo: "coleta" | "entrega";
  control: Control<TFieldValues>;
  preco: number;
  cepValue: string;
  complementoValue?: string;
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
  onCalculate: () => Promise<void>;
  showComplemento?: boolean;
  defaultExpanded?: boolean;
}

export function CepOptionCard<TFieldValues extends FieldValues = FieldValues>({
  tipo,
  control,
  preco,
  cepValue,
  complementoValue,
  selected,
  onSelectedChange,
  onCalculate,
  showComplemento = true,
  defaultExpanded = false,
}: Props<TFieldValues>) {
  void complementoValue;
  const isColeta = tipo === "coleta";
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [showInput, setShowInput] = useState(defaultExpanded);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isMobile) {
      setShowInput(true);
    }
  }, [isMobile]);

  const toggleInput = () => {
    if (!isMobile) return;
    setShowInput((prev) => !prev);
  };

  const handleToggleOption = () => {
    if (preco == 0) {
      setErrorMessage("Por favor, calcule o CEP antes de selecionar");
      setTimeout(() => setErrorMessage(""), 5000);
      return;
    }
    if (!cepValue || cepValue.length < 8) {
      setErrorMessage("Informe o CEP para selecionar");
      setTimeout(() => setErrorMessage(""), 5000);
      return;
    }
    onSelectedChange(!selected);
  };

  const handleCalcularCep = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      await onCalculate();
    } catch {
      setErrorMessage("Erro ao calcular CEP. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const formatCep = (value: string) => {
    const onlyNums = value.replace(/\D/g, "");
    if (onlyNums.length <= 5) return onlyNums;
    return `${onlyNums.slice(0, 5)}-${onlyNums.slice(5, 8)}`;
  };

  const label = isColeta
    ? "Adicionar Coleta no seu endereço"
    : "Adicionar Entrega no seu endereço";

  const cepFieldName = isColeta ? "cepColeta" : "cepEntrega";
  const complementoFieldName = isColeta
    ? "complementoColeta"
    : "complementoEntrega";

  return (
    <div
      className={`w-full flex flex-col gap-3 rounded-2xl border border-zinc-300 py-3 px-6 text-n-primary transition-colors ${
        selected ? "bg-n-bg-1" : "bg-white"
      }`}
    >
      <div className="flex gap-3 items-start">
        <Checkbox
          className="mt-0.5 flex-shrink-0"
          checked={selected}
          onCheckedChange={handleToggleOption}
        />
        <div className="flex flex-1 flex-col gap-1">
          <div
            className={`flex items-center justify-between gap-2 ${
              isMobile ? "cursor-pointer" : ""
            }`}
            onClick={isMobile ? toggleInput : undefined}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm text-gray-900">{label}</span>
              <span className="text-sm text-gray-600">
                {preco > 0
                  ? formatMoneyBr(preco)
                  : `A partir de ${formatMoneyBr(300)}`}
              </span>
              <CustomPopover
                iconSize={4}
                title={isColeta ? "Endereço de Coleta" : "Endereço de Entrega"}
                description={
                  isColeta
                    ? "Local específico para retirada do veículo."
                    : "Local específico para entrega do veículo."
                }
              />
            </div>
            {isMobile && (
              <div className="ml-2 flex-shrink-0">
                {showInput ? (
                  <ChevronUp className="h-4 w-4 text-n-green" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-n-green" />
                )}
              </div>
            )}
          </div>

          {showInput && (
            <div className="flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center gap-2 mt-2">
              <Controller
                name={cepFieldName as Path<TFieldValues>}
                control={control}
                render={({ field }) => (
                  <div className="relative w-full md:w-[130px]">
                    <Input
                      placeholder="90905-000"
                      maxLength={9}
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => {
                        const formatted = formatCep(e.target.value);
                        field.onChange(formatted);
                      }}
                      className="text-sm max-h-[28px] px-3 pr-8 rounded-full bg-gray-50 border-gray-300 py-0 w-full"
                    />
                    <LuPenLine className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  </div>
                )}
              />

              {showComplemento && (
                <Controller
                  name={complementoFieldName as Path<TFieldValues>}
                  control={control}
                  render={({ field }) => (
                    <FormItem className="flex-1 w-full md:min-w-[150px]">
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="Complemento"
                            {...field}
                            value={field.value || ""}
                            className="max-h-[28px] pr-8 rounded-full bg-gray-50 border-gray-300 py-0 w-full"
                          />
                          <LuPenLine className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <Button
                type="button"
                size="md"
                onClick={handleCalcularCep}
                disabled={loading || !cepValue || cepValue.length < 8}
                className="px-6 py-0 text-sm max-h-[28px] min-h-[28px] rounded-full w-full md:w-auto"
                variant="green"
              >
                {loading ? "Calculando..." : "Calcular"}
              </Button>
            </div>
          )}

          {errorMessage && (
            <div className="flex items-center gap-1 text-xs mt-1">
              <FaRegDotCircle className="h-2 w-2 text-n-red" />
              <span className="text-n-red">{errorMessage}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
